// Não é permitido atualização de versão de uma major para outra; tanto downgrade quanto upgrade (ex: 2.x.x para 3.x.x ou vice versa)
// com exceção para famílias 1 e 2 dos dispositivos 'dac2', 'dac3', 'dac4', 'dut', 'dut3', 'dam2' e 'dam3' (ex: 1.x.x para a 2.x.x)

import { DeviceRow, FirmwareRow } from './types';

// e dispositivos sem versão (tageados), em que nesse caso podem atualizar apenas para versão que seja refatorada
export function checkCanUpdate(currFwInfo: DeviceRow, wantedFwInfo: FirmwareRow) {
  const devId = currFwInfo.DEV_ID;

  // Se não soubermos a versão atual do dispositivo, não permite atualizar
  const currMajor = currFwInfo.versionNumber?.vMajor ?? null;
  const newMajor = wantedFwInfo.versionNumber?.vMajor ?? null;
  if ((!currMajor) || (!currFwInfo.fwInfo)) {
    return { canUpdate: false, outMsg: `${devId} com versão desconhecida` };
  }

  // Restrição pelo DEVID e coluna. Dispositivos só devem ser capazes de atualizar suas versões de acordo com a coluna certa com as iniciais igual ao DEVID.
  // "fwFamily" é a pasta (do S3) que contém o arquivo de firmware e é também o nome da coluna na tela de firmwares.
  if ((wantedFwInfo.fwFamily.length === 4) && devId.toLowerCase().startsWith(wantedFwInfo.fwFamily.toLowerCase())) {
    // OK, caso padrão
  } else if ((wantedFwInfo.fwFamily === 'dut') && ['DUT0', 'DUT1', 'DUT2'].includes(devId.toUpperCase().substring(0, 4))) {
    // OK, dispositivos com o DEVID iniciado em DUT0, DUT1 e DUT2 podem receber firmware da coluna DUT
  } else if ((wantedFwInfo.fwFamily === 'dma0') && ['DMA1'].includes(devId.toUpperCase().substring(0, 4))) {
    // OK, dispositivos com DEVID iniciado em DMA1 podem receber versões de firmware da coluna DMA0
  } else {
    return { canUpdate: false, outMsg: `Não pode atualizar ${devId} pois a pasta do firmware (${wantedFwInfo.path}) não corresponde ao dev_id` };
  }

  // Legado tem formato: "1_3_4"
  // Refatorado tem formato: "v3.2.0", "v4.1.1-1-g21fcc67-dirty", "d80e580-dirty", "085427f"
  // Observei que atualmente versão major 1 e 2 é sempre legado, refatorado é a partir de versão major 3.
  const currentVersion = currFwInfo.fwInfo?.firmware_version;
  const newIsLegacy = wantedFwInfo.fwVers.includes('_');
  const currentIsLegacy = currentVersion?.includes('_');
  const currentIsRefactor = !currentIsLegacy;

  const newIsRefactor = !newIsLegacy;
  const isSameLegacyRefactor = newIsRefactor === currentIsRefactor || newIsLegacy === currentIsLegacy;

  // Não é permitido atualização de versão de uma major para outra; tanto downgrade quanto upgrade (ex: 2.x.x para 3.x.x ou vice versa)
  // Compatibilidade de Versões de major. Versões de firmware na linha 2_x_x, v3.x.x ou v4.x.x só podem receber versões da mesma linha.
  // Dispositivos só podem ser atualizados de refatorado para refatorado e de legado para legado
  if (!isSameLegacyRefactor) {
    return {
      canUpdate: false,
      outMsg: `Não pode atualizar ${devId} entre legado e refatorado. Versão ${currentVersion} para ${wantedFwInfo.fwVers}`,
    };
  }

  if (newMajor === currMajor) {
    // OK, caso padrão
  } else if ((currMajor === 1) && (newMajor === 2) && ['DAC2', 'DAC3', 'DAC4'].includes(devId.toUpperCase().substring(0, 4))) {
    // OK, dispositivos com o DEVID iniciado em DAC2, DAC3 e DAC4 E versão de firmware 1_x_x podem receber versões de firmware na linha 2_x_x
    // Antes esta regra se aplicava também a: 'dut', 'dut3', 'dam2', 'dam3'
  } else if ((!currMajor) && newIsRefactor) {
    // OK, dispositivos sem versão (tageados) podem atualizar apenas para versão que seja refatorada
  } else {
    return { canUpdate: false, outMsg: `Não pode atualizar ${devId} mudando da versão ${currentVersion} para ${wantedFwInfo.fwVers}` };
  }

  return { canUpdate: true, outMsg: '' };
}

function isCompatibleFirmwareUpdate(current, wanted): boolean {
  // Verifica se a família do firmware bate com a família do dispositivo
  if (!wanted.path.includes(current.devIdFamily) || !wanted.refName) {
    return false;
  }

  const wantedPath = wanted.refName;

  // Regex para extrair padrões de migração como: <family>_mig_<from>_to_<to>
  const migrationRegex = new RegExp(`${current.devIdFamily}_mig_([a-zA-Z0-9_]+)_to_([a-zA-Z0-9_]+)`, 'i');
  const match = wantedPath.match(migrationRegex);

  if (!match) {
    return false; // Nome do arquivo não segue padrão esperado
  }

  const from = match[1];

  // Converte versão atual para algo comparável com o "from"
  const normalizeVersion = (v): string => {
    if (v.vMajor >= 3) return `v${v.vMajor}`; // refatorado
    return `${v.vMajor}_${v.vMinor}_${v.vPatch}`; // legado
  };

  const currentFormatted = normalizeVersion(current.versionNumber);

  // Verifica se a versão atual bate com a versão "from" do arquivo
  const isFromCompatible = from === currentFormatted || from === `${current.versionNumber.vMajor}`;

  return isFromCompatible;
}

// Deve ser permitido atualizar de uma versão de firmware para outra, desde que a versão seja compatível com o dispositivo e a versão atual.
// Versões de firmware no formato 2_x_x (legado) podem ser atualizadas para versões no formato v3.x.x ou v4.x.x (refatorado)
// As permissões estão definidas no nome do arquivo de firmware.
// Ex: dac4_mig_1_to_v3.bin -> permite atualizar de 1_x_x para v3.x.x
export function checkCanUpdateMigration(
  currFwInfo: DeviceRow,
  wantedFwInfo: FirmwareRow,
): { canUpdate: boolean; outMsg: string } {
  const firmwareCompatibility = isCompatibleFirmwareUpdate(currFwInfo, wantedFwInfo);

  if (!firmwareCompatibility) {
    return {
      canUpdate: false,
      outMsg: `Não pode atualizar ${currFwInfo.DEV_ID} para ${wantedFwInfo.fwVers} pois a versão não é compatível com o dispositivo`,
    };
  }

  return {
    canUpdate: true,
    outMsg: '',
  };
}
